// The Tally server as the web app sees it. Same origin: the session lives in an HttpOnly cookie, and every change
// carries `X-Tally-Client`, which cross-site pages cannot send (backend/src/app.ts checks it).
import type { NewInvoice, ProStatus, Role, StripeInvoice, StripeStatus, TeamResponse,
  Base, Change, Me, Membership, PushStatus, RangeResponse, RawEvent, SessionResponse, TimeEntryVersion, WorkspacesResponse, WorkspaceSummary, InviteResult,
} from './types.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** The server's explanation, where it has one for people (Stripe's reason for a refusal). */
  readonly detail: string | null;

  constructor(status: number, code: string, detail: string | null = null) {
    super(code);
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

export interface LiveHandlers {
  onChange(seq: number): void;
  onConnected(connected: boolean): void;
}

export type AuthConfig = { provider: 'email' } | { provider: 'clerk'; publishableKey: string };

export interface Api {
  readonly demo: boolean;
  /** Without an account: data only in this browser (guest.ts); Pro and everything server-side need an account. */
  readonly guest: boolean;
  me(): Promise<Me | null>;
  setWorkspace(id: string | null): void;
  workspaces(): Promise<WorkspacesResponse>;
  createWorkspace(name: string): Promise<WorkspaceSummary>;
  selectWorkspace(workspaceId: string): Promise<WorkspaceSummary>;
  acceptInvite(id: string): Promise<WorkspaceSummary>;
  declineInvite(id: string): Promise<void>;
  renameWorkspace(name: string): Promise<void>;
  leaveWorkspace(): Promise<void>;
  startEmail(email: string, locale: string): Promise<void>;
  verifyEmail(email: string, code: string): Promise<{ created: boolean; workspace: Membership | null }>;
  verifyLink(token: string): Promise<{ created: boolean; workspace: Membership | null }>;
  logout(): Promise<void>;
  claimWorkspace(ids: { workspaceId: string; memberId: string; name: string; memberName: string }): Promise<Membership>;
  base(): Promise<Base>;
  range(start: number, end: number, usage: boolean): Promise<RangeResponse>;
  session(id: string): Promise<SessionResponse | null>;
  events(sessionId: string): Promise<RawEvent[]>;
  versions(entryId: string): Promise<TimeEntryVersion[]>;
  tags(): Promise<string[]>;
  push(changes: Change[]): Promise<PushStatus[]>;
  addDevice(name: string, platform: string): Promise<{ id: string; token: string }>;
  removeDevice(id: string): Promise<void>;
  exportAccount(): Promise<Blob>;
  deleteAccount(): Promise<void>;
  /** Redeems a developer code (spec/pro.md §5): Pro for good on this account, as the server decides. */
  redeemProCode(code: string): Promise<{ pro: ProStatus }>;
  /** The team: owners invite, change roles and remove; members see who is in it. */
  team(): Promise<TeamResponse>;
  invite(email: string, role: Role, locale: string): Promise<InviteResult>;
  revokeInvite(id: string): Promise<void>;
  setRole(memberId: string, role: Role): Promise<void>;
  removeMember(memberId: string): Promise<void>;
  /** Stripe invoices (owners): the connection, invoices and their state. */
  stripe(): Promise<StripeStatus>;
  connectStripe(secretKey: string): Promise<StripeStatus>;
  disconnectStripe(): Promise<void>;
  invoices(): Promise<StripeInvoice[]>;
  createInvoice(invoice: NewInvoice): Promise<StripeInvoice>;
  refreshInvoice(id: string): Promise<StripeInvoice>;
  /** Server-Sent Events: a change anywhere in the workspace. Returns a function that disconnects. */
  live(handlers: LiveHandlers): () => void;
}

/** The server accepts at most 1000 changes per push. */
const PUSH_BATCH = 500;

export class HttpApi implements Api {
  readonly demo = false;
  readonly guest = false;
  private readonly origin: string;
  private workspaceId: string | null = null;

  constructor(origin = '') {
    this.origin = origin;
  }

  setWorkspace(id: string | null): void { this.workspaceId = id; }

  private async request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal, workspaceId = this.workspaceId): Promise<T> {
    const headers: Record<string, string> = { 'X-Tally-Client': 'web' };
    if (workspaceId && path !== '/v1/workspace/claim' && /^\/v1\/(workspace(?:\/|$)|data\/|sync\/|devices(?:\/|$)|sessions\/|stripe(?:\/|$)|invoices(?:\/|$))/.test(path)) headers['X-Tally-Workspace'] = workspaceId;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    let response: Response;
    try {
      response = await fetch(this.origin + path, {
        method, headers, credentials: 'same-origin', signal, ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch {
      throw new ApiError(0, 'offline');
    }
    if (!response.ok) {
      let code = `http_${response.status}`;
      let detail: string | null = null;
      try {
        const json = (await response.json()) as { error?: string; message?: string };
        if (json.error) code = json.error;
        if (typeof json.message === 'string') detail = json.message;
      } catch { /* not JSON */ }
      throw new ApiError(response.status, code, detail);
    }
    return (await response.json()) as T;
  }

  async me(): Promise<Me | null> {
    try {
      return await this.request<Me>('GET', '/v1/me');
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  }

  async authConfig(): Promise<AuthConfig> {
    const config = await this.request<AuthConfig>('GET', '/v1/auth/config', undefined, AbortSignal.timeout(10_000));
    if (config.provider === 'email') return config;
    if (config.provider === 'clerk' && typeof config.publishableKey === 'string' && /^pk_(test|live)_[A-Za-z0-9_-]+$/.test(config.publishableKey)) return config;
    throw new ApiError(503, 'auth_unavailable');
  }

  verifyClerk(token: string) {
    return this.request<{ created: boolean; workspace: Membership | null }>('POST', '/v1/auth/clerk', { token, web: true, workspaceMode: 'multiple' });
  }

  async startEmail(email: string, locale: string): Promise<void> {
    await this.request('POST', '/v1/auth/email/start', { email, locale });
  }

  verifyEmail(email: string, code: string) {
    return this.request<{ created: boolean; workspace: Membership | null }>('POST', '/v1/auth/email/verify', { email, code, web: true, workspaceMode: 'multiple' });
  }

  verifyLink(token: string) {
    return this.request<{ created: boolean; workspace: Membership | null }>('POST', '/v1/auth/email/verify', { token, web: true, workspaceMode: 'multiple' });
  }

  workspaces() { return this.request<WorkspacesResponse>('GET', '/v1/workspaces'); }
  createWorkspace(name: string) { return this.request<WorkspaceSummary>('POST', '/v1/workspaces', { name }); }
  selectWorkspace(workspaceId: string) { return this.request<WorkspaceSummary>('PUT', '/v1/workspaces/active', { workspaceId }); }
  acceptInvite(id: string) { return this.request<WorkspaceSummary>('POST', `/v1/workspaces/invites/${encodeURIComponent(id)}/accept`, {}); }
  async declineInvite(id: string): Promise<void> { await this.request('DELETE', `/v1/workspaces/invites/${encodeURIComponent(id)}`); }
  async renameWorkspace(name: string): Promise<void> { await this.request('PATCH', '/v1/workspace', { name }); }
  async leaveWorkspace(): Promise<void> { await this.request('POST', '/v1/workspace/leave', {}); }

  async logout(): Promise<void> {
    await this.request('POST', '/v1/auth/logout', {});
  }

  claimWorkspace(ids: { workspaceId: string; memberId: string; name: string; memberName: string }) {
    return this.request<Membership>('POST', '/v1/workspace/claim', ids);
  }

  base() {
    return this.request<Base>('GET', '/v1/data/base');
  }

  range(start: number, end: number, usage: boolean) {
    return this.request<RangeResponse>('GET', `/v1/data/range?start=${start}&end=${end}${usage ? '&usage=1' : ''}`);
  }

  async session(id: string): Promise<SessionResponse | null> {
    try {
      return await this.request<SessionResponse>('GET', `/v1/data/sessions/${encodeURIComponent(id)}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  }

  async events(sessionId: string): Promise<RawEvent[]> {
    return (await this.request<{ events: RawEvent[] }>('GET', `/v1/sessions/${encodeURIComponent(sessionId)}/events`)).events;
  }

  async versions(entryId: string): Promise<TimeEntryVersion[]> {
    return (await this.request<{ versions: TimeEntryVersion[] }>('GET', `/v1/data/entries/${encodeURIComponent(entryId)}/versions`)).versions;
  }

  async tags(): Promise<string[]> {
    return (await this.request<{ tags: string[] }>('GET', '/v1/data/tags')).tags;
  }

  async push(changes: Change[]): Promise<PushStatus[]> {
    const results: PushStatus[] = [];
    const workspaceId = this.workspaceId;
    for (let i = 0; i < changes.length; i += PUSH_BATCH) {
      const batch = changes.slice(i, i + PUSH_BATCH);
      const response = await this.request<{ results: { status: PushStatus }[] }>('POST', '/v1/sync/push', { changes: batch }, undefined, workspaceId);
      results.push(...response.results.map((r) => r.status));
    }
    return results;
  }

  async addDevice(name: string, platform: string) {
    const id = crypto.randomUUID();
    const response = await this.request<{ deviceToken: string }>('POST', '/v1/devices', { id, name, platform });
    return { id, token: response.deviceToken };
  }

  async removeDevice(id: string): Promise<void> {
    await this.request('DELETE', `/v1/devices/${encodeURIComponent(id)}`);
  }

  async exportAccount(): Promise<Blob> {
    const response = await fetch(`${this.origin}/v1/me/export`, { credentials: 'same-origin', headers: { 'X-Tally-Client': 'web' } });
    if (!response.ok) throw new ApiError(response.status, `http_${response.status}`);
    return response.blob();
  }

  redeemProCode(code: string): Promise<{ pro: ProStatus }> {
    return this.request<{ pro: ProStatus }>('POST', '/v1/pro/redeem', { code });
  }

  async deleteAccount(): Promise<void> {
    await this.request('DELETE', '/v1/me', { confirm: 'DELETE' });
  }

  team() {
    return this.request<TeamResponse>('GET', '/v1/workspace/team');
  }

  invite(email: string, role: Role, locale: string): Promise<InviteResult> {
    return this.request<InviteResult>('POST', '/v1/workspace/invites', { email, role, locale, sendEmail: false });
  }

  async revokeInvite(id: string): Promise<void> {
    await this.request('DELETE', `/v1/workspace/invites/${encodeURIComponent(id)}`);
  }

  async setRole(memberId: string, role: Role): Promise<void> {
    await this.request('PATCH', `/v1/workspace/members/${encodeURIComponent(memberId)}`, { role });
  }

  async removeMember(memberId: string): Promise<void> {
    await this.request('DELETE', `/v1/workspace/members/${encodeURIComponent(memberId)}`);
  }

  stripe() {
    return this.request<StripeStatus>('GET', '/v1/stripe');
  }

  connectStripe(secretKey: string) {
    return this.request<StripeStatus>('PUT', '/v1/stripe', { secretKey });
  }

  async disconnectStripe(): Promise<void> {
    await this.request('DELETE', '/v1/stripe');
  }

  async invoices(): Promise<StripeInvoice[]> {
    return (await this.request<{ invoices: StripeInvoice[] }>('GET', '/v1/invoices')).invoices;
  }

  createInvoice(invoice: NewInvoice) {
    return this.request<StripeInvoice>('POST', '/v1/invoices', invoice);
  }

  refreshInvoice(id: string) {
    return this.request<StripeInvoice>('POST', `/v1/invoices/${encodeURIComponent(id)}/refresh`);
  }

  live(handlers: LiveHandlers): () => void {
    const scope = this.workspaceId ? `?workspaceId=${encodeURIComponent(this.workspaceId)}` : '';
    let source: EventSource | null = null;
    const seq = (event: MessageEvent) => {
      try {
        return Number((JSON.parse(String(event.data)) as { seq?: number }).seq ?? 0);
      } catch {
        return 0;
      }
    };
    const connect = () => {
      if (source) return;
      const next = new EventSource(`${this.origin}/v1/live${scope}`, { withCredentials: true });
      source = next;
      next.addEventListener('hello', (event) => {
        if (source !== next) return;
        handlers.onConnected(true);
        handlers.onChange(seq(event as MessageEvent));
      });
      next.addEventListener('change', (event) => { if (source === next) handlers.onChange(seq(event as MessageEvent)); });
      next.onerror = () => { if (source === next) handlers.onConnected(false); };
    };
    const disconnect = () => { source?.close(); source = null; };
    const restore = (event: PageTransitionEvent) => {
      if (!event.persisted || source) return;
      connect();
      handlers.onChange(0);
    };
    const page = typeof window === 'undefined' ? undefined : window;
    connect();
    page?.addEventListener('pagehide', disconnect);
    page?.addEventListener('pageshow', restore);
    return () => {
      page?.removeEventListener('pagehide', disconnect);
      page?.removeEventListener('pageshow', restore);
      disconnect();
    };
  }
}
